import { useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { getGraphClient } from "../services/graphClient";
import { listVendorFolders, listPOsForVendor } from "../services/sharepoint";

const STATUSES = ["Ordered", "Partially Received", "Received", "Waiting for Invoice", "Closed / Invoiced"];

export function useStatusView(driveId) {
  const { instance, accounts } = useMsal();
  const [statusPOs, setStatusPOs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({
    "Ordered": "…",
    "Partially Received": "…",
    "Received": "…",
    "Waiting for Invoice": "…",
    "Closed / Invoiced": "…",
  });

  const fetchByStatus = useCallback(async (status) => {
    if (!driveId) return;
    setLoading(true);
    setStatusPOs([]);
    try {
      const client = getGraphClient(instance, accounts[0]);
      const folders = await listVendorFolders(client, driveId);
      const allFiles = await Promise.all(
        folders.map(async (f) => {
          const files = await listPOsForVendor(client, driveId, f.name).catch(() => []);
          return files.map((file) => ({ ...file, vendorName: f.name }));
        })
      );
      const flat = allFiles.flat();
      const filtered = flat.filter(
        (f) => (f.fields?.Status || "Ordered") === status
      );
      setStatusPOs(filtered);
    } catch (e) {
      console.error("[useStatusView] fetchByStatus error:", e);
    } finally {
      setLoading(false);
    }
  }, [driveId, instance, accounts]);

  const fetchCounts = useCallback(async () => {
    if (!driveId) return;
    try {
      const client = getGraphClient(instance, accounts[0]);
      const folders = await listVendorFolders(client, driveId);

      // Fetch all folders in parallel with individual error handling
      const allFiles = await Promise.all(
        folders.map((f) =>
          listPOsForVendor(client, driveId, f.name)
            .then((files) => files.map((file) => ({ ...file, vendorName: f.name })))
            .catch(() => [])
        )
      );
      const flat = allFiles.flat();

      // Count each status — default to "Ordered" if no status set
      const c = {};
      STATUSES.forEach((s) => {
        c[s] = flat.filter((f) => (f.fields?.Status || "Ordered") === s).length;
      });
      setCounts(c);
    } catch (e) {
      console.error("[useStatusView] fetchCounts error:", e);
      // Show 0 for all on failure rather than …
      const c = {};
      STATUSES.forEach((s) => { c[s] = 0; });
      setCounts(c);
    }
  }, [driveId, instance, accounts]);

  return { statusPOs, loading, counts, fetchByStatus, fetchCounts };
}
